import { useEffect, useState } from 'react';
import { userAppContext } from '@/context/Context';
import fetchEndpoint from '@/services/fetchEndpoint';
import { GtpDetailCard } from '@/components/GtpBreakdown/GtpDetailCard';
import { getGtpItemsFromRow, type GeneGtpItem } from '@/utils/formatGtpList';
import { getGeneIdCandidates } from '@/utils/openGtpRowDetail';
import Loader from '@/components/Common/Loader';

type GtpTabContentProps = {
  row: Record<string, unknown> | null | undefined;
  cellValues?: Record<string, unknown> | null;
  className?: string;
};

function resolveGeneId(
  row: Record<string, unknown> | null | undefined,
  cellValues?: Record<string, unknown> | null,
): string {
  const fromRow = getGeneIdCandidates(row)[0];
  if (fromRow) return fromRow;
  return getGeneIdCandidates(cellValues)[0] ?? '';
}

function resolveInlineGtp(
  row: Record<string, unknown> | null | undefined,
  cellValues?: Record<string, unknown> | null,
): GeneGtpItem[] {
  const fromRow = getGtpItemsFromRow(row);
  if (fromRow.length > 0) return fromRow;
  return getGtpItemsFromRow(cellValues);
}

export function GtpTabContent({ row, cellValues = null, className }: GtpTabContentProps) {
  const userCtx = (userAppContext() ?? {}) as {
    authorization?: string;
    isAuthenticated?: boolean;
    setIsAuthenticated?: (value: boolean) => void;
  };
  const { authorization, isAuthenticated, setIsAuthenticated } = userCtx;

  const inlineItems = resolveInlineGtp(row, cellValues);
  const geneId = resolveGeneId(row, cellValues);

  const [fetchedItems, setFetchedItems] = useState<GeneGtpItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (inlineItems.length > 0) {
      setFetchedItems(null);
      setLoading(false);
      setError(false);
      return;
    }
    if (!geneId) {
      setFetchedItems([]);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    setFetchedItems(null);

    const unauth = isAuthenticated ? '' : '?skey=unauth';
    const endpoint = `api/gene/detail/${encodeURIComponent(geneId)}${unauth}`;

    fetchEndpoint(endpoint, authorization, setIsAuthenticated)
      .then((response: { status?: string; payload?: Record<string, unknown> } | null) => {
        if (cancelled) return;
        if (response?.status === 'OK' && response.payload) {
          setFetchedItems(getGtpItemsFromRow(response.payload));
        } else {
          setFetchedItems([]);
          setError(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedItems([]);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authorization, geneId, inlineItems.length, isAuthenticated, setIsAuthenticated]);

  if (inlineItems.length > 0) {
    return <GtpDetailCard items={inlineItems} className={className} />;
  }

  if (loading || fetchedItems === null) {
    return (
      <div
        className="flex min-h-[120px] items-center justify-center"
        aria-busy="true"
        aria-label="Loading GTAP"
      >
        <Loader isInnerSpinner />
      </div>
    );
  }

  if (error && fetchedItems.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-sm text-gray-400">
        Could not load GTAP profile for this gene.
      </p>
    );
  }

  return <GtpDetailCard items={fetchedItems} className={className} />;
}
